/* eslint react-hooks/exhaustive-deps: "warn" */

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Badge } from 'ui/atoms/Badge/Badge';
import type { BadgeVariant } from 'ui/atoms/Badge/Badge.types';
import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './MonitoringFeedBanner.module.scss';

export type MonitoringFeedItem = {
  company: string;
  parts: { text: string; bold?: boolean; muted?: boolean; strikethrough?: boolean }[];
  tag: string;
  tagVariant: BadgeVariant;
  time: string;
};

const DEFAULT_ITEMS: MonitoringFeedItem[] = [
  {
    company: 'Revolut Ltd',
    parts: [
      { text: 'Revolut Ltd', bold: true },
      { text: ' — New director appointed: ' },
      { text: 'James K. Morrison', bold: true },
    ],
    tag: 'Director',
    tagVariant: 'violet',
    time: '2m ago',
  },
  {
    company: 'Meridian Supply GmbH',
    parts: [
      { text: 'Meridian Supply GmbH', bold: true },
      { text: ' — Status changed: ' },
      { text: 'Active', bold: true },
      { text: ' to ' },
      { text: 'In Liquidation', bold: true },
    ],
    tag: 'Status',
    tagVariant: 'rose',
    time: '8m ago',
  },
  {
    company: 'Tesco PLC',
    parts: [{ text: 'Tesco PLC', bold: true }, { text: ' — New annual accounts filed (FY 2025)' }],
    tag: 'Financial',
    tagVariant: 'emerald',
    time: '14m ago',
  },
  {
    company: 'Nova Energia S.r.l.',
    parts: [
      { text: 'Nova Energia S.r.l.', bold: true },
      { text: ' — UBO changed: ownership crossed ' },
      { text: '50% threshold', bold: true },
    ],
    tag: 'Owner',
    tagVariant: 'orange',
    time: '21m ago',
  },
  {
    company: 'CloudFirst Technologies Ltd',
    parts: [
      { text: 'CloudFirst Technologies Ltd', bold: true },
      { text: ' — Name changed from ' },
      { text: 'DataVault Solutions Ltd', muted: true, strikethrough: true },
    ],
    tag: 'Name',
    tagVariant: 'indigo',
    time: '34m ago',
  },
  {
    company: 'Nexus Pharma B.V.',
    parts: [
      { text: 'Nexus Pharma B.V.', bold: true },
      { text: ' — Registered address changed to ' },
      { text: 'Amsterdam, NL', bold: true },
    ],
    tag: 'Address',
    tagVariant: 'cyan',
    time: '47m ago',
  },
];

type Props = {
  items?: MonitoringFeedItem[];
};

export const MonitoringFeedBanner = ({ items = DEFAULT_ITEMS }: Props) => {
  const { t } = useInt();

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>{t('Monitoring feed')}</span>

        <div className={styles.live}>
          <span className={styles.liveDot}>
            <span className={styles.liveDotInner} />
          </span>
          <span className={styles.liveLabel}>{t('Live')}</span>
        </div>
      </div>

      <div className={styles.feed}>
        {items.map(({ company, parts, tag, tagVariant, time }) => (
          <div key={company} className={styles.item}>
            <Icon type="i-161" className={styles.itemIcon} />

            <div className={styles.itemContent}>
              <div className={styles.itemMain}>
                <p className={styles.itemText}>
                  {parts.map(({ text, bold, muted, strikethrough }, idx) => (
                    <span
                      key={idx}
                      className={cn(
                        bold && styles.textBold,
                        muted && styles.textMuted,
                        strikethrough && styles.textStrike,
                      )}
                    >
                      {text}
                    </span>
                  ))}
                </p>

                <Badge size="sm" variant={tagVariant}>
                  {tag}
                </Badge>
              </div>

              <div className={styles.itemTime}>
                <span className={styles.timeIcon}>
                  <Icon type="duration" />
                </span>

                <span className={styles.timeLabel}>{time}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
